import { useEffect, useMemo } from "react";
import numToRoman from "../../../../helpers/numToRoman";
import { getUnits } from "../../../../utils/helpers";
import CustomTooltip from "../../../../utils/tooltip/tooltip";
import useStore from "../../../../hooks/useStore";
import { calculateWaterData } from "../../../../helpers/groupDataCalculations";
import info from "../../../info.svg";
import { observer } from "mobx-react";

const accumulateResult = (data, key) =>
  data.reduce((acc, curr) => acc + (Number(curr[key]) || 0), 0);


const Row = ({ data, index, frequency, setData, group, totalData }) => {
  console.log("totalData", frequency);
  const {
    companyStore: { units, kpiTarget },
  } = useStore();
  const onChangeHandler = (event) => {
    const { name, value, files } = event.target;
    setData((prev) =>
      calculateWaterData(
        prev.map((ele) => {
          if (ele.group === group) {
            const temp = ele.value.map((e) => {
              if (e.fieldId === data.fieldId) {
                return {
                  ...e,
                  ...frequency,
                  [name]:
                    name === "attachment"
                      ? [...(e.attachment || []), ...Array.from(files || [])] // Properly convert FileList to an array
                      : value,
                };
              }
              return e;
            });
            return {
              ...ele,
              value: temp,
              target: accumulateResult(temp, "target"),
              prevValue: accumulateResult(temp, "prevValue"),
            };
          }
          return ele;
        })
      )
    );
  };

  const cancelHandler = (file) => {
    setData((prev) =>
      prev.map((ele) => {
        if (ele.group === group) {
          const temp = ele.value.map((e) => {
            if (e.fieldId === data.fieldId) {
              const updatedAttachments = e.attachment.filter(
                (item) => item.name !== file.name || item.lastModified !== file.lastModified
              );
              return {
                ...e,
                ...frequency,
                attachment: updatedAttachments,
              };
            }
            return e;
          });
          return {
            ...ele,
            value: temp,
          };
        }
        return ele;
      })
    );
  };

  const isDisabled = useMemo(
    () =>
      [
        "Percentage of water recycle/reuse: (Total water quantity recycled and reused /  Water withdrawal by source)*100",
        "( Total Water consumption / Finished Goods Quantity Produced )",
        "( Total Water consumption / Revenue )",
      ].includes(data.fieldName),
    [data.fieldName]
  );
  const revenue =
    units
      ?.find(
        (ele) =>
          ele.id ===
          totalData?.find((ele) => ele.group === "D")?.value?.[0].unit
      )
      ?.name?.split(" ") || [];

  const revenueUnit =
    revenue?.length === 1 ? "rupees" : revenue[revenue.length - 1];

  const finishedGoods = units?.find(
    (ele) =>
      ele.id ===
      totalData
        ?.find((e) => e.group === "D")
        ?.value?.find((e) => e.fieldName === "Finished Goods Quantity Produced")
        ?.unit
  )?.shortName;

  const unitField = units.find(
    (ele) =>
      ele.id === totalData?.find((ele) => ele.group === "A")?.value?.[0]?.unit
  )?.shortName;

  const dataUnits =
    data.fieldName === "( Total Water consumption / Revenue )"
      ? getUnits({ units, data, type: "water" }).filter(
        (ele) =>
          ele.name.includes(
            ["dollar", "rupees", "euro"].includes(revenueUnit)
              ? revenueUnit
              : ""
          ) && ele.name.includes(unitField || "")
      )
      : data.fieldName ===
        "( Total Water consumption / Finished Goods Quantity Produced )"
        ? getUnits({ units, data, type: "water" }).filter(
          (ele) =>
            ele.shortName.includes(
              ["MT", "Kg"].includes(finishedGoods) ? finishedGoods : ""
            ) && ele.shortName.includes(unitField || "")
        )
        : getUnits({ units, data, type: "water" });

useEffect(() => {
  if (!data) return;

  const match = kpiTarget?.find(
    (kt) => kt.fieldId === data?.fieldId && kt.group === data?.group
  );

  let dividedTarget = 0;

  if (match?.target) {
    dividedTarget = match.target;
    if (frequency?.frequencyType === "Monthly") {
      dividedTarget = match.target / 12;
    } else if (frequency?.frequencyType === "Daily") {
      const year = Number(
        frequency?.frequencyYear || new Date(frequency?.frequencyDate).getFullYear()
      );
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      dividedTarget = match.target / (isLeapYear ? 366 : 365);
    }
  }

  setData((prev) =>
    calculateWaterData(
      prev.map((ele) => {
        if (ele.group === group) {
          const updated = ele.value.map((e) => {
            if (e.fieldId === data.fieldId) {
              return {
                ...e,
                target: dividedTarget.toFixed(2), // Set even if 0
              };
            }
            return e;
          });

          return {
            ...ele,
            value: updated,
            target: accumulateResult(updated, "target") || 0,
            prevValue: accumulateResult(updated, "prevValue"),
          };
        }
        return ele;
      })
    )
  );
}, [
  kpiTarget,
  data?.fieldId,
  data?.group,
  setData,
  group,
  frequency?.frequencyType,
  frequency?.frequencyYear,
  frequency?.frequencyDate,
]);

  return (
    <tr key={`${data?.id}-data-row`}>
      <td>{index || index === 0 ? numToRoman(index + 1) : "*"}</td>
      <td>
        <span
          style={{
            display: "flex",
            gap: "5px",
          }}
        >
          {data?.fieldName}
          {data?.guidance && (
            <CustomTooltip position="top" content={data?.guidance}>
              <img src={info} alt="" />
            </CustomTooltip>
          )}
        </span>
      </td>
      <td>
        <select
          className="form-select"
          name="unit"
          required
          onChange={onChangeHandler}
          value={data?.unitId || data.unit}
          style={{ width: "135px" }}
        >
          <option value="">Select Unit</option>
          {dataUnits?.map((ele) => (
            <option key={ele.id} value={ele.id}>
              {ele?.shortName}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div>{frequency?.frequencyType || ""}</div>
        <div>{frequency?.frequencyYear || ""}</div>
        <div>{frequency?.frequencyDate || frequency?.frequencyMonth || ""}</div>
      </td>
      <td>
        <input
          disabled={isDisabled}
          type={isDisabled ? "text" : "number"}
          name="prevValue"
          onChange={onChangeHandler}
          value={data.prevValue}
          className="rounded form-control w-100 numberCountInput"
        />
      </td>
      {data.group !== "D" && (
        <>
          <td>
            <input
              disabled={isDisabled}
              type={isDisabled ? "text" : "number"}
              name="target"
              onChange={onChangeHandler}
              value={data.target}
              className="rounded form-control w-100 numberCountInput"
            />
          </td>
          <td>
            <textarea
              name="comment"
              value={data.comment}
              onChange={onChangeHandler}
              className="rounded form-control w-100 addFromTxtArea"
            />
          </td>

          <td>
            {data.attachment && data.attachment.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                {data.attachment.map((file, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    className="fileUploader"
                  >
                    <span>{file?.name.slice(0, 10) + "..."}</span>
                    <span
                      onClick={() => cancelHandler(file)}
                      style={{
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "16px",
                        lineHeight: "20px",
                      }}
                      className="crossBtn"
                    >
                      X
                    </span>
                  </div>
                ))}
                
              </div>
            )}
              <span
                w-100
                style={{
                  position: "relative",
                }}
              >
                <div
                  className="chooseFile"
                  style={{
                    position: "absolute",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Choose File
                </div>
                <input
                  name="attachment"
                  onChange={onChangeHandler}
                  type="file"
                  multiple
                  style={{
                    opacity: 0,
                    cursor: "pointer",
                    zIndex: 1,
                    top: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  className="form-control w-100 h-100"
                />
              </span>
           
          </td>
        </>
      )}
    </tr>
  );
};

export default observer(Row);
